import axios from 'axios';
import EmailCampaignAudienceContentTab from '../email/EmailCampaignAudienceContentTab';
import OpportunityConvertToContactTab from '../../custom/shared/OpportunityConvertToContactTab';
import { API_BASE_URL } from '../../constants/CONSTANT';
import { crmAuthHeaders } from '../../api/DataApi';
import UserWelcomeEmailTab from '../user/UserWelcomeEmailTab';
import UserMfaAdminTab from '../user/UserMfaAdminTab';
import WatconnectAdSettingsTab from '../watconnect/adsettings/WatconnectAdSettingsTab';

/**
 * Static map: component_key → React component (must match component_registry.component_key when allowlist is enforced).
 */
const STATIC_REGISTRY = {
  EmailCampaignAudienceTab: EmailCampaignAudienceContentTab,
  EmailCampaignContentTab: EmailCampaignAudienceContentTab,
  OpportunityConvertToContactTab,
  UserWelcomeEmailTab,
  UserMfaAdminTab,
  WatconnectAdSettingsTab,
};

export default STATIC_REGISTRY;

/**
 * Active registry rows for the tenant, optionally scoped by object API name.
 * @param {AbortSignal} [signal]
 * @param {string|null|undefined} forObject e.g. opportunity — omit or empty for all universal + unscoped rows (unless matchObjectOnly)
 * @param {{ matchObjectOnly?: boolean }} [options] if true, only components whose object_name matches forObject (no universal rows); empty forObject → []
 * @returns {Promise<Array<{ component_key: string, display_label?: string, description?: string, sort_order?: number, object_name?: string|null }>>}
 */
export async function fetchUiComponentRegistryRows(signal, forObject, options = {}) {
  try {
    const params = new URLSearchParams();
    if (forObject != null && String(forObject).trim() !== '') {
      params.set('targetObject', String(forObject).trim());
    }
    if (options.matchObjectOnly) {
      params.set('matchObjectOnly', 'true');
    }
    const qs = params.toString();
    const url = `${API_BASE_URL}/api/ui-component-registry${qs ? `?${qs}` : ''}`;
    const res = await axios.get(url, {
      headers: { ...crmAuthHeaders() },
      signal,
    });
    if (!res.data?.success || !Array.isArray(res.data.data)) {
      return [];
    }
    return res.data.data;
  } catch (e) {
    console.warn('[ComponentRegistry] ui-component-registry:', e?.message || e);
    return [];
  }
}

/**
 * Fetch active keys from tenant component_registry.
 * @param {AbortSignal} [signal]
 * @param {string|null|undefined} forObject when set, allowlist only rows for that object (and universal rows).
 * @returns {Promise<Set<string>|null>} Set to enforce allowlist, or null if no rows / fetch failed (all static keys allowed).
 */
export async function fetchUiComponentRegistryAllowlist(signal, forObject) {
  const rows = await fetchUiComponentRegistryRows(signal, forObject);
  if (rows.length === 0) {
    return null;
  }
  return new Set(rows.map((r) => r.component_key).filter(Boolean));
}

/**
 * @param {string|null|undefined} componentKey
 * @param {Set<string>|null|undefined} allowlist - from fetchUiComponentRegistryAllowlist; null/undefined = no DB restriction
 * @returns {{ Component: React.ComponentType|null, blockedReason: 'no_implementation'|'not_activated'|null }}
 */
export function resolveRegisteredTabComponent(componentKey, allowlist) {
  if (!componentKey) {
    return { Component: null, blockedReason: 'no_implementation' };
  }
  const Component = STATIC_REGISTRY[componentKey];
  if (!Component) {
    return { Component: null, blockedReason: 'no_implementation' };
  }
  if (allowlist && allowlist.size > 0 && !allowlist.has(componentKey)) {
    return { Component: null, blockedReason: 'not_activated' };
  }
  return { Component, blockedReason: null };
}
